import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Box, Flex, Text } from "@chakra-ui/react";
import useNumberFormatter from "../../../../hooks/useNumberFormatter";
import CustomTooltip from "./CustomTooltip";

type DataPoint = {
  day: string;
  currentMonth: number | undefined;
  previousMonth: number | undefined;
};

type Props = {
  data: DataPoint[];
};

const CollectionComparativeGraph = ({ data }: Props): JSX.Element => {
  const formatNumberFormatter = useNumberFormatter("es-CO", "COP", "number");

  const formatNumber = (
    value: string | number | (string | number)[]
  ): string => {
    if (Array.isArray(value)) {
      return value.map(formatSingleValue).join(", ");
    }
    return formatSingleValue(value);
  };

  const formatSingleValue = (val: string | number): string => {
    return typeof val === "number" ? formatNumberFormatter(val) : val;
  };

  return (
    <Box width="100%" height="auto">
      {/* Title and Legend Container */}
      <Flex justifyContent="space-between" alignItems="flex-start" mb={4}>
        <Box>
          <Text fontSize="1.1rem" fontWeight="600" color="#1A202C">
            Recaudo Mensual
          </Text>
          <Text color="#858D9D" fontSize="0.85rem" fontWeight="500">
            Comparativa día a día
          </Text>
        </Box>

        {/* Custom Legend */}
        <Flex alignItems="center" gap={4}>
          {/* Previous Month Legend Item */}
          <Flex alignItems="center" gap={2}>
            <Box w={4} h={2} borderRadius="4px" bg="#E2E8F0" />
            <Text fontSize="0.85rem" fontWeight="600" color="#54596C">
              Mes anterior
            </Text>
          </Flex>

          {/* Current Month Legend Item */}
          <Flex alignItems="center" gap={2}>
            <Box w={4} h={2} borderRadius="4px" bg="#3B338D" />
            <Text fontSize="0.85rem" fontWeight="600" color="#1A202C">
              Mes actual
            </Text>
          </Flex>
        </Flex>
      </Flex>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={255}>
        <BarChart
          data={data}
          margin={{ top: 20, right: 30, left: 80, bottom: 20 }}
          barCategoryGap="15%"
          barGap={0}
        >
          <XAxis
            dataKey="day"
            tick={{ fill: "#A0AEC0", fontSize: 12, fontWeight: 500 }}
            axisLine={false}
            tickLine={false}
            label={{
              value: "", // Removing axis label for cleaner look in new design
              position: "insideBottom",
              offset: -10,
              dy: 10,
            }}
          />
          <YAxis
            tick={{ fill: "#A0AEC0", fontSize: 12, fontWeight: 500 }}
            axisLine={false}
            tickLine={false}
            label={{
              value: "", // Removing axis label for cleaner look in new design
              angle: -90,
              position: "insideLeft",
              offset: -35,
              dx: -35,
            }}
            tickFormatter={(value) => formatNumber(value)}
          />
          <Tooltip
            content={(tooltipProps) => (
              <CustomTooltip {...tooltipProps} formatter={formatNumber} />
            )}
            cursor={{ fill: "#F7FAFC" }}
          />
          {/* <Legend /> */}
          <Bar
            dataKey="previousMonth"
            fill="#E2E8F0"
            name="Mes anterior"
            radius={[4, 4, 0, 0]}
          />
          <Bar
            dataKey="currentMonth"
            fill="#3B338D"
            name="Mes actual"
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default CollectionComparativeGraph;
